// Identity persisted in localStorage. Every access is guarded: storage can be unavailable
// (private mode, blocked site data) and the app must still work.

const PLAYER_ID_KEY = "minesbrawl:id";
const NAME_KEY = "minesbrawl:name";
const LANG_KEY = "minesbrawl:lang";
const DURATION_KEY = "minesbrawl:duration";

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore: identity just won't survive a reload.
  }
}

let memoryPlayerId: string | null = null;

export function getPlayerId(): string {
  const stored = read(PLAYER_ID_KEY);
  if (stored) return stored;
  const id = memoryPlayerId ?? uuidV4();
  memoryPlayerId = id;
  write(PLAYER_ID_KEY, id);
  return id;
}

export function getName(): string {
  return read(NAME_KEY) ?? "";
}

export function setName(name: string): void {
  write(NAME_KEY, name);
}

/** Explicit UI language choice (validated by the caller); null means "follow the browser". */
export function getLangPreference(): string | null {
  return read(LANG_KEY);
}

export function setLangPreference(lang: string): void {
  write(LANG_KEY, lang);
}

/** Last round length picked when creating a room, in seconds (validated by the caller). */
export function getDurationPreference(): number | null {
  const stored = read(DURATION_KEY);
  return stored === null ? null : Number(stored);
}

export function setDurationPreference(seconds: number): void {
  write(DURATION_KEY, String(seconds));
}

/**
 * crypto.randomUUID only exists in secure contexts, and local testing from a phone
 * over http://<lan-ip> is not one. getRandomValues works everywhere.
 */
function uuidV4(): string {
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const hex = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
